import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../../../../../api/api";
import { getPracticeId } from "../../../../../config/practice";

const HomesleepLink = () => {
  const { patientid, filename } = useParams();

  const [loading, setLoading] = useState(true);
  const [json, setJson] = useState(null);

  const practiseid = getPracticeId();

  // backend screenshot waits for this
  const SCREENSHOT_TARGET_ID = "screenshot-ready-container";

  useEffect(() => {
    const fetchHtml = async () => {
      try {
        const response = await api.get(
          `/v1/${practiseid}/pcard/${patientid}/formHistory/${filename}`
        );

        if (response.status === 200) {
          setJson(response.data?.json_data || null);
        }
      } catch (error) {
        console.log(error);
      } finally {
        // small delay for layout stabilization
        setTimeout(() => setLoading(false), 300);
      }
    };

    fetchHtml();
  }, [filename]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen text-xl font-semibold">
        Loading...
      </div>
    );
  }

  if (!json) {
    return (
      <div className="flex items-center justify-center h-screen text-xl text-red-600">
        No Data Found
      </div>
    );
  }

  // Utility arrays -----------------------------------

  const epworthRows = [
    { label: "Sitting and reading", name: "qanswer_6_17" },
    { label: "Watching TV", name: "qanswer_6_18" },
    { label: "Sitting inactive in a public place", name: "qanswer_6_19" },
    { label: "Passenger in car 1 hr", name: "qanswer_6_20" },
    { label: "Lying down afternoon", name: "qanswer_6_21" },
    { label: "Talking to someone", name: "qanswer_6_22" },
    { label: "After lunch without alcohol", name: "qanswer_6_23" },
    { label: "Stopped in traffic", name: "qanswer_6_24" },
  ];

  const comorbidConditions = [
    "Hypertension",
    "Stroke",
    "PLMD",
    "RBD",
    "Leg Jerking",
    "Diabetes",
    "Sleep talking",
    "Sleepwalking",
    "Neuromuscular Disorder",
    "COPD",
    "Epilepsy/Seizure",
    "CHF",
    "Coronary artery disease",
    "Bruxism (grinding teeth)",
    "Unspecified Insomnia",
  ];

  const renderYesNo = (value) => (
    <div className="flex space-x-4 mt-2">
      {["YES", "NO"].map((opt) => (
        <span
          key={opt}
          className={`px-4 py-2 rounded-full text-sm font-sans ${
            json[value] === opt
              ? "bg-blue-600 text-white border border-blue-600"
              : "bg-white border-2 border-gray-300 text-black"
          }`}
        >
          {opt}
        </span>
      ))}
    </div>
  );

  // -----------------------------------------------------

  return (
    <>
    

      {/* Page Background */}
      <div id={json.patient_name?SCREENSHOT_TARGET_ID:undefined} className="min-h-screen bg-[#f7f7f7] flex justify-center py-10 px-3">

        {/* Center Card */}
        <div className="bg-white shadow-lg text-start rounded-lg p-6 max-w-4xl w-full">

          <h2 className="text-2xl font-bold mb-4">{json.form_name}</h2>

          {/* Patient Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="font-bold">Patient Name</label>
              <p>{json.patient_name}</p>
            </div>
            <div>
              <label className="font-bold">Date</label>
              <p>{json.cdate}</p>
            </div>
            <div>
              <label className="font-bold">Provider Name</label>
              <p>{json.provider_name}</p>
            </div>
            <div>
              <label className="font-bold">DOB</label>
              <p>{json.dob}</p>
            </div>
          </div>

          {/* Vitals */}
          <h3 className="font-bold text-xl mt-4">Vitals</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
            {[
              { label: "HT", value: json.ptheight },
              { label: "WT", value: json.ptweight },
              { label: "BMI", value: json.ptbmi },
              { label: "BP", value: json.ptbp },
              { label: "NC", value: json.ptnc },
            ].map((item, idx) => (
              <div key={idx}>
                <label className="font-bold">{item.label}</label>
                <p>{item.value}</p>
              </div>
            ))}
          </div>

          {/* Insomnia */}
          <h3 className="font-bold text-xl mt-4">Insomnia</h3>
          {[
            { q: "Using a CPAP machine?", k: "qanswer_6_2" },
            { q: "Trouble falling asleep?", k: "qanswer_6_3" },
            { q: "Trouble staying asleep?", k: "qanswer_6_4" },
          ].map((i, idx) => (
            <div key={idx} className="mb-4">
              <label className="font-bold">{i.q}</label>
              {renderYesNo(i.k)}
            </div>
          ))}

          {/* OSA */}
          <h3 className="font-bold text-xl mt-4">OSA Evaluation</h3>
          {[
            { q: "Do you snore?", k: "qanswer_6_5" },
            { q: "Gasping/Choking arousals?", k: "qanswer_6_6" },
            { q: "20% Weight Change?", k: "qanswer_6_7" },
            { q: "Urge to move legs?", k: "qanswer_6_8" },
            { q: "Tingling legs?", k: "qanswer_6_9" },
            { q: "Worse when lying down?", k: "qanswer_6_10" },
          ].map((i, idx) => (
            <div key={idx} className="mb-4">
              <label className="font-bold">{i.q}</label>
              {renderYesNo(i.k)}
            </div>
          ))}

          {/* Daytime Sleepiness */}
          <h3 className="font-bold text-xl mt-4">Daytime Sleepiness</h3>
          {[
            { q: "Feeling sleepy?", k: "qanswer_6_11" },
            { q: "Tired, low energy?", k: "qanswer_6_12" },
            { q: "Difficulty concentrating?", k: "qanswer_6_13" },
            { q: "Napping on purpose?", k: "qanswer_6_14" },
            { q: "Sleepy while driving?", k: "qanswer_6_15" },
            { q: "Dozing at work?", k: "qanswer_6_16" },
          ].map((i, idx) => (
            <div key={idx} className="mb-4">
              <label className="font-bold">{i.q}</label>
              {renderYesNo(i.k)}
            </div>
          ))}

          {/* Epworth */}
          <h3 className="font-bold text-xl mt-4">Epworth Sleepiness Scale</h3>

          <table className="w-full border-collapse mb-4">
            <thead>
              <tr>
                <th></th>
                {[0, 1, 2, 3].map((v) => (
                  <th key={v} className="text-center">{v}</th>
                ))}
              </tr>
            </thead>

            <tbody>
              {epworthRows.map((item, idx) => (
                <tr key={idx}>
                  <td className="py-2 font-semibold">{item.label}</td>
                  {[0, 1, 2, 3].map((v) => (
                    <td key={v} className="text-center">
                      <span
                        className={`px-3 py-1 rounded-full ${
                          Number(json[item.name]) === v
                            ? "bg-blue-600 text-white"
                            : "bg-white border-2 border-gray-300"
                        }`}
                      >
                        {v}
                      </span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>

          {/* Comorbid Conditions */}
          <h3 className="font-bold text-xl mt-4">Related Comorbid Conditions</h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-10">
            {comorbidConditions.map((cond, idx) => (
              <label key={idx} className="flex items-center">
                <input
                  type="checkbox"
                  readOnly
                  checked={
                    Array.isArray(json.qanswer_6_25) &&
                    json.qanswer_6_25.includes(cond)
                  }
                  className="mr-2"
                />
                {cond}
              </label>
            ))}
          </div>

        </div>
      </div>
    </>
  );
};

export default HomesleepLink;
